import { Slot } from '@radix-ui/react-slot';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef, type ReactNode } from 'react';

import s from './Bleed.module.css';

/**
 * Spacing scale values for the bleed margin.
 * Common values for IconButton alignment:
 * - 200 = 8px (sm IconButton)
 * - 250 = 10px (md IconButton)
 * - 300 = 12px (lg IconButton)
 */
export const BLEED_MARGINS = [
  '025',
  '050',
  '100',
  '150',
  '200',
  '250',
  '300',
  '400',
  '500',
  '600',
] as const;

export type BleedMargin = (typeof BLEED_MARGINS)[number];

interface BleedProps extends ComponentPropsWithRef<'div'> {
  /** When true, merges props onto the child element. When false, wraps with a div. */
  asChild?: boolean;
  children: ReactNode;
  /** Negative horizontal (inline) margin on both sides */
  marginInline?: BleedMargin;
  /** Negative vertical (block) margin on both sides */
  marginBlock?: BleedMargin;
  /** Negative top (block-start) margin */
  marginBlockStart?: BleedMargin;
  /** Negative bottom (block-end) margin */
  marginBlockEnd?: BleedMargin;
  /** Negative left (inline-start) margin */
  marginInlineStart?: BleedMargin;
  /** Negative right (inline-end) margin */
  marginInlineEnd?: BleedMargin;
}

export const Bleed = (props: BleedProps) => {
  const {
    asChild = true,
    children,
    className,
    marginInline,
    marginBlock,
    marginBlockStart,
    marginBlockEnd,
    marginInlineStart,
    marginInlineEnd,
    ...attrs
  } = props;

  if (!children) {
    return null;
  }

  const Component = asChild ? Slot : 'div';

  return (
    <Component
      className={clsx(
        marginInline && s[`marginInline${marginInline}`],
        marginBlock && s[`marginBlock${marginBlock}`],
        marginBlockStart && s[`marginBlockStart${marginBlockStart}`],
        marginBlockEnd && s[`marginBlockEnd${marginBlockEnd}`],
        marginInlineStart && s[`marginInlineStart${marginInlineStart}`],
        marginInlineEnd && s[`marginInlineEnd${marginInlineEnd}`],
        className,
      )}
      {...attrs}
    >
      {children}
    </Component>
  );
};
